import React, { useState, useRef, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { spaService } from '../services/spaService';
import LoadingSpinner from '../components/LoadingSpinner';
import { Calendar } from 'primereact/calendar';

const RideResults = () => {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(null);
  const [editingRows, setEditingRows] = useState({});
  const toast = useRef(null);
  

  const rowExpansionTemplate2 = (data) => {
    const rideResults = data?.rideResults;
    
    if (!rideResults || !rideResults.riderResults || rideResults.riderResults.length === 0) {
      return (
        <div className="p-3">
          <Message 
            severity="info" 
            text="No rider results recorded yet for this event."
            className="w-full"
          />
        </div>
      );
    }

    return (
      <div className="p-3">
        <div className="grid">
          <div className="col-12">
            <div className="se-form-group">              
              <DataTable 
                value={rideResults.riderResults} 
                editMode="row" 
                dataKey="id"
                onRowEditSave={onRowEditSave}
                onRowEditCancel={onRowEditCancel}
                onRowEditInit={onRowEditInit}
                editingRows={editingRows}
                onRowEditChange={(e) => setEditingRows(e.data)}
              >
                <Column field="bibNo" header="Bib" sortable editor={opts => textEditor({ ...opts, keyfilter: 'pint' })} />
                <Column field="riderName" header="Rider Name" />
                <Column field="membership" header="Membership" />
                <Column 
                  field="horse.horseName" 
                  header="Horse"
                  body={(rowData) => rowData.horse?.horseName || '-'}
                />
                <Column field="mobile" header="Mobile" />
                <Column 
                  field="arrived"
                  header="Arrived" 
                  body={arrivedBodyTemplate}
                  editor={arrivedEditor}
                  style={{ width: '100px' }}
                  headerStyle={{ width: '100px' }}
                />
                <Column field="mileage" header="Mileage" body={milesBodyTemplate} editor={opts => textEditor({ ...opts, showSuffix: true, keyfilter: 'pnum' })} />
                <Column rowEditor headerStyle={{ width: '10%', minWidth: '8rem' }} bodyStyle={{ textAlign: 'center' }} />
              </DataTable>
            </div>
          </div>
        </div>
        <div className="flex justify-content-end mt-3">
          <Button
            label="Unlock & Edit"
            icon="pi pi-unlock"
            className="p-button-warning"
            onClick={() => handleUnlockResults(data)}
          />
        </div>
      </div>
    );
  };

  const [isUnlocked, setIsUnlocked] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const ridesResult = await spaService.getAllRides();

      if (Array.isArray(ridesResult)) {
        setRides(ridesResult);
      } else {
        setError('Unexpected response format from server');
      }
    } catch (err) {
      console.error('Error loading rides:', err);
      setError(err.message || 'An error occurred while loading rides data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return '-';
    
    try {
      if (dateObj.singleDate?.dateString) {
        return dateObj.singleDate.dateString;
      }
      
      if (dateObj.singleDate?.dateValue) {
        const date = new Date(dateObj.singleDate.dateValue);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
      
      if (Array.isArray(dateObj.multipleDates) && dateObj.multipleDates.length > 0) {
        const firstDate = dateObj.multipleDates[0];
        if (firstDate.dateString) return firstDate.dateString;
        if (firstDate.dateValue) {
          const date = new Date(firstDate.dateValue);
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      }
      
      if (typeof dateObj === 'string') {
        const date = new Date(dateObj);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
      
      return '-';
    } catch (err) {
      console.error('Date parsing error:', err, dateObj);
      return '-';
    }
  };

  const arrivedBodyTemplate = (rowData) => {
    return (
      <InputSwitch 
        checked={rowData.arrived || false}
        disabled
      />
    );
  };

  const arrivedEditor = (options) => {
    return (
      <InputSwitch 
        checked={options.value || false}
        onChange={(e) => options.editorCallback(e.value)}
      />
    );
  };

  const textEditor = (options) => {
    return <div className="relative">
      <InputText type="text" keyfilter={options.keyfilter} value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />
      {options.showSuffix &&
          <span className="top-0 absolute right-0 pr-3 py-3">miles</span>}
  </div>
  };
  const timeEditor = (options) => {
    return <div className="relative">
      <InputText type='time' value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />
  </div>
  };

  const timeOutBodyTemplate = (rowData) => {
    return rowData.out || '-';
  };
  
  const timeInBodyTemplate = (rowData) => {
    return rowData.in || '-';
  };

  const milesBodyTemplate = (rowData) => {
    return rowData.mileage ? rowData.mileage + " miles" : '-';
  };

  const dateBodyTemplate = (rowData) => {
    return formatDate(rowData.date);
  };

  const handleSubmitFinalResults = async (rideData) => {
    confirmDialog({
      message: 'Are you sure you want to submit final results? This will move the ride to completed status and lock the results from further editing.',
      header: 'Confirm Finalize Results',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          const rideResultsId = rideData.rideResults?.id;
          
          if (!rideResultsId) {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: 'Could not find ride results ID',
              life: 3000
            });
            return;
          }

          const result = await spaService.finalizeResults(rideResultsId);
          
          if (result.success) {
            // Update local state to reflect finalized status
            setRides(prevRides => 
              prevRides.map(ride => 
                ride.eventId === rideData.eventId 
                  ? { 
                      ...ride, 
                      rideResults: {
                        ...ride.rideResults,
                        isFinalized: true
                      }
                    }
                  : ride
              )
            );
            
            // Collapse the expanded row
            setExpandedRows(null);
            toast.current?.show({
              severity: 'success',
              summary: 'Success',
              detail: 'Results submitted successfully! Ride moved to Completed section.',
              life: 3000
            });
          } else {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: result.error || 'Failed to finalize results',
              life: 3000
            });
          }
        } catch (err) {
          console.error('Error finalizing results:', err);
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to finalize results: ' + err.message,
            life: 3000
          });
        }
      }
    });
  };

  const onRowEditSave = async (e) => {
    let { newData, index } = e;
    
    try {
      const updateData = {
        riderResultId: newData.id,
        bibNo: newData.bibNo ?? '',
        arrived: newData.arrived || false,
        in: newData.in || '',
        out: newData.out || '',
        mileage: newData.mileage || ''
      };

      const result = await spaService.updateRiderResult(updateData);
      
      if (result.success) {
        // Update the rides state with the updated result and recalculate totalArrived
        setRides(prevRides => 
          prevRides.map(ride => {
            if (ride.rideResults?.riderResults) {
              // Update the rider result
              const updatedRiderResults = ride.rideResults.riderResults.map(rr => 
                rr.id === newData.id ? { ...rr, ...result.result } : rr
              );
              
              // Recalculate totalArrived based on updated rider results
              const totalArrived = updatedRiderResults.filter(rr => rr.arrived).length;
              
              return {
                ...ride,
                totalArrived: totalArrived,
                rideResults: {
                  ...ride.rideResults,
                  riderResults: updatedRiderResults
                }
              };
            }
            return ride;
          })
        );

        setIsUnlocked(false);
        
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Rider result saved successfully!',
          life: 3000
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.error || 'Failed to save rider result',
          life: 3000
        });
      }
    } catch (err) {
      console.error('Error saving rider result:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save rider result: ' + err.message,
        life: 3000
      });
    }
  };

  const onRowEditCancel = (e) => {
    // No action needed for cancel
    setIsUnlocked(false);
  };

  const onRowEditInit = (e) => {
    // No action needed for init
  };

  const handleDownloadCSV = async (rideData) => {
    try {
      const rideResultsId = rideData.rideResults?.id;
      
      if (!rideResultsId) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not find ride results ID',
          life: 3000
        });
        return;
      }

      // Generate filename from event name and date
      const eventName = rideData.eventName || 'ride';
      const dateStr = formatDate(rideData.date).replace(/\//g, '-');
      const fileName = `${eventName.replace(/[^a-z0-9]/gi, '-')}_${dateStr}.csv`;

      await spaService.downloadRideCSV(rideResultsId, fileName);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'CSV file downloaded successfully',
        life: 3000
      });
    } catch (err) {
      console.error('Error downloading CSV:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to download CSV: ' + err.message,
        life: 3000
      });
    }
  };

  const handleCancelEvent = async (rideData) => {
    confirmDialog({
      message: `Are you sure you want to cancel all orders for this event? This will cancel and refund all ${rideData.totalBooked} booking(s) for "${rideData.eventName}". This action cannot be undone.`,
      header: 'Cancel Event - Confirm',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          const eventId = rideData.eventId;
          
          if (!eventId) {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: 'Could not find event ID',
              life: 3000
            });
            return;
          }

          toast.current?.show({
            severity: 'info',
            summary: 'Processing',
            detail: 'Cancelling event orders...',
            life: 3000
          });

          const result = await spaService.cancelEvent(eventId);
          
          if (result.success) {
            // Reload data to refresh the view
            await loadData();
            
            const successCount = result.results?.filter(r => r.Success).length || 0;
            const failCount = result.results?.filter(r => !r.Success).length || 0;
            
            if (failCount > 0) {
              toast.current?.show({
                severity: 'warn',
                summary: 'Partially Completed',
                detail: `${successCount} order(s) cancelled successfully. ${failCount} order(s) failed.`,
                life: 5000
              });
            } else {
              toast.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: `Event cancelled successfully! ${result.totalOrders} order(s) cancelled and refunded.`,
                life: 5000
              });
            }
          } else {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: result.error || 'Failed to cancel event',
              life: 3000
            });
          }
        } catch (err) {
          console.error('Error cancelling event:', err);
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to cancel event: ' + err.message,
            life: 5000
          });
        }
      }
    });
  };

  const handleUnlockResults = async (rideData) => {
    setIsUnlocked(true);
  };

  const rowExpansionTemplate = (data) => {
    const rideResults = data.rideResults;
    
    if (!rideResults || !rideResults.riderResults || rideResults.riderResults.length === 0) {
      return (
        <div className="p-3">
          <Message 
            severity="info" 
            text="No rider results recorded yet for this event."
            className="w-full"
          />
        </div>
      );
    }

    return (
      <div className="p-3">
        <div className="grid">
          <div className="col-12">
            <div className="se-form-group">
              <DataTable 
                value={rideResults.riderResults} 
                editMode="row" 
                dataKey="id"
                onRowEditSave={onRowEditSave}
                onRowEditCancel={onRowEditCancel}
                onRowEditInit={onRowEditInit}
                editingRows={editingRows}
                onRowEditChange={(e) => setEditingRows(e.data)}
              >
                <Column field="bibNo" header="Bib" sortable editor={opts => textEditor({ ...opts, keyfilter: 'pint' })} />
                <Column field="riderName" header="Rider Name" />
                <Column field="membership" header="Membership" />
                <Column 
                  field="horse.horseName" 
                  header="Horse"
                  body={(rowData) => rowData.horse?.horseName || '-'}
                />
                <Column field="mobile" header="Mobile" />
                <Column 
                  field="arrived"
                  header="Arrived" 
                  body={arrivedBodyTemplate}
                  editor={arrivedEditor}
                  style={{ width: '100px' }}
                  headerStyle={{ width: '100px' }}
                />
                <Column field="in" header="In" body={timeInBodyTemplate} editor={timeEditor} />
                <Column field="out" header="Out" body={timeOutBodyTemplate} editor={timeEditor} />
                <Column field="mileage" header="Mileage" body={milesBodyTemplate} editor={opts => textEditor({ ...opts, showSuffix: true, keyfilter: 'pnum' })} />
                <Column rowEditor headerStyle={{ width: '10%', minWidth: '8rem' }} bodyStyle={{ textAlign: 'center' }} />
              </DataTable>
            </div>
          </div>
        </div>
        {!rideResults.isFinalized && <div className="flex justify-content-end mt-3">
          <Button
            label="Submit Final Results"
            icon="pi pi-check"
            className="p-button-success"
            onClick={() => handleSubmitFinalResults(data)}
          />
        </div>}
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading ride results..." />;
  }

  const awaitingEntry = rides.filter(ride => !ride.rideResults?.isFinalized);
  const completed = rides.filter(ride => ride.rideResults?.isFinalized);


  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="se-card">
        <div className="se-card-header">
          <h2>Ride & Results Manager</h2>
        </div>
        <div className="se-card-content">
          
          {error && (
            <Message 
              severity="error" 
              text={error}
              className="mb-3 w-full"
            />
          )}
          
          <h3>Awaiting Entry</h3>
          
          {awaitingEntry.length === 0 ? (
            <Message 
              severity="info" 
              text="No rides awaiting entry."
              className="mb-3 w-full"
            />
          ) : (
            <DataTable 
              value={awaitingEntry} 
              responsiveLayout="scroll" 
              className="mt-3"
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="eventId"
            >
              <Column expander allowExpansion />
              <Column field="eventName" header="Event Name" />
              <Column field="date" header="Date" body={dateBodyTemplate} />
              <Column field="location" header="Location" />
              <Column field="totalBooked" header="Booked" />
              <Column field="totalArrived" header="Arrived" />
              <Column 
                header="Actions" 
                body={(rowData) => (
                  <div className="flex gap-1">
                    {rowData.extraInfoLink && <Button
                      icon="pi pi-eye"
                      label="View"
                      className="p-button-sm p-button-text"
                      onClick={() => window.open(rowData.extraInfoLink, '_blank')}
                      tooltip="View Document"
                      tooltipOptions={{ position: 'top' }}
                    />}
                    <Button
                      icon="pi pi-download"
                      label="CSV"
                      className="p-button-sm p-button-text"
                      onClick={() => handleDownloadCSV(rowData)}
                      tooltip="Download CSV"
                      tooltipOptions={{ position: 'top' }}
                    />
                    <Button
                      icon="pi pi-times"
                      label="Cancel Event"
                      className="p-button-sm p-button-danger p-button-text"
                      onClick={() => handleCancelEvent(rowData)}
                      tooltip="Cancel all orders for this event"
                      tooltipOptions={{ position: 'top' }}
                    />
                  </div>
                )}
                style={{ width: '320px' }}
              />
            </DataTable>
          )}
          
          <h3 className="mt-5">Completed Rides</h3>
          
          {completed.length === 0 ? (
            <Message 
              severity="info" 
              text="No completed rides yet."
              className="mb-3 w-full"
            />
          ) : (
            <DataTable 
              value={completed} 
              responsiveLayout="scroll" 
              className="mt-3"
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={isUnlocked ? rowExpansionTemplate : rowExpansionTemplate2}
              dataKey="eventId"
            >
              <Column expander allowExpansion />
              <Column field="eventName" header="Event Name" />
              <Column field="date" header="Date" body={dateBodyTemplate} sortable />
              <Column field="location" header="Location" />
              <Column field="totalBooked" header="Booked" />
              <Column field="totalArrived" header="Attended" />
              <Column 
                header="Actions" 
                body={(rowData) => (
                  <>
                  {rowData.extraInfoLink && <Button
                    icon="pi pi-eye"
                    label="View"
                    className="p-button-sm p-button-text mr-2"
                    onClick={() => window.open(rowData.extraInfoLink, '_blank')}
                    tooltip="View Document"
                    tooltipOptions={{ position: 'top' }}
                  />}
                  <Button
                    icon="pi pi-download"
                    label="CSV"
                    className="p-button-sm p-button-text"
                    onClick={() => handleDownloadCSV(rowData)}
                    tooltip="Download CSV"
                    tooltipOptions={{ position: 'top' }}
                  /></>
                )}
                style={{ width: '240px' }}
              />
            </DataTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default RideResults;
